import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import { LightDiskButton } from 'apps/promo/components/common/fromAuth/LightDiskButton/LightDiskButton';
import { PromoText } from 'apps/promo/components/common/promo-text/PromoText';
import { toLowerCase } from 'apps/promo/helpers/utils';
import { useMatchMedia } from 'apps/promo/hooks/useMatchMedia';
import promoBreakpoints from 'apps/promo/theme/promoBreakpoints';

import PromoCountryItem from './PromoCountryItem';
import PromoCurrencyList from './PromoCurrencyList';
import { CountryListDropdown, DropdownList, DropdownListInner, DropdownNotFound, SearchInput, SearchInputWrapper } from './styles';

const PromoCountryList = (props) => {
    const isDesktop = useMatchMedia(promoBreakpoints.desktop1440);
    const direction = props.role === 'debit' ? 'send' : 'receive';
    const DEFAULT_COUNTRY_HEIGHT = isDesktop ? 68 : 56;
    const NO_RESULT_HEIGHT = 72;
    const COUNT_ITEM_FOR_RESIZE = 5;
    const DEFAULT_SCROLL_HEIGHT = DEFAULT_COUNTRY_HEIGHT * COUNT_ITEM_FOR_RESIZE;

    const [scrollHeight, setScrollHeight] = useState(DEFAULT_SCROLL_HEIGHT);

    const [isEmptySearchResults, setIsEmptySearchResults] = useState(false);

    const [COUNTRIES, CURRENCIES] = ['countries', 'currencies'];
    const displayedContentOptions = [COUNTRIES, CURRENCIES];
    const [displayedContent, setDisplayedContent] = useState(COUNTRIES);

    const availableCountries = props.calculatorData.countries.filter((country) => country[direction]);

    const [countries, setCountries] = useState(availableCountries);
    const [currencies, setCurrencies] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [currentCountryIndex, setCurrentCountryIndex] = useState(null);

    const itemRefs = useRef([]);
    const searchInputRef = useRef(null);
    const dropdownListRef = useRef(null);

    useEffect(() => {
        // Focus on the search input.
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }

        // Add event listener for clicks outside of the dropdown.
        const handleClickOutside = (event) => {
            if (dropdownListRef.current && !dropdownListRef.current.contains(event.target)) {
                props.toggleCountryList();
            }
        };

        // Add event listener for keyboard navigation in the dropdown.
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowDown' && displayedContent === COUNTRIES) {
                setCurrentCountryIndex((prevIndex) => {
                    return prevIndex === null ? 0 : Math.min(prevIndex + 1, countries.length - 1);
                });
            } else if (event.key === 'ArrowUp' && displayedContent === COUNTRIES) {
                setCurrentCountryIndex((prevIndex) => {
                    return prevIndex === null ? countries.length - 1 : Math.max(prevIndex - 1, 0);
                });
            } else if (event.key === 'Enter' && currentCountryIndex !== null && displayedContent === COUNTRIES) {
                const country = countries[currentCountryIndex];
                const currencies = country.currencies.filter((currency) => currency[direction]);
                handleCountrySelection(country, currencies);
            } else if (event.key === 'Escape') {
                props.toggleCountryList();
            }
        };

        if (currentCountryIndex !== null && itemRefs.current[currentCountryIndex]) {
            itemRefs.current[currentCountryIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [currentCountryIndex, countries]);

    const filterCountries = (event) => {
        const searchValue = toLowerCase(event.target.value);
        const filteredCountries = availableCountries.filter((country) => {
            let combinedData = [
                country.name,
                country.code,
                country.alias,
                ...country.currencies.filter((currency) => currency[direction]).map((currency) => currency.code),
            ].join(' ');

            return toLowerCase(combinedData).includes(searchValue);
        });
        setCountries(filteredCountries);

        const isFilteredCountriesEmpty = filteredCountries.length === 0;

        const getScrollHeight = () => {
            if (filteredCountries.length <= COUNT_ITEM_FOR_RESIZE && filteredCountries.length !== 0) {
                return DEFAULT_COUNTRY_HEIGHT * filteredCountries.length;
            } else {
                return isFilteredCountriesEmpty ? NO_RESULT_HEIGHT : DEFAULT_SCROLL_HEIGHT;
            }
        };

        setIsEmptySearchResults(isFilteredCountriesEmpty);
        setScrollHeight(getScrollHeight);
        setCurrentCountryIndex(null);
    };

    const toggleDisplayedContent = () => {
        const currentIndex = displayedContentOptions.indexOf(displayedContent);
        const nextIndex = (currentIndex + 1) % displayedContentOptions.length;
        setDisplayedContent(displayedContentOptions[nextIndex]);
    };

    const handleCurrencySelection = (currency) => {
        props.onDropdownChange(props.role, selectedCountry.code, currency.code);
        props.toggleCountryList();
    };

    const handleCountrySelection = (country, currencies) => {
        props.onSelect?.(selectedCountry, country);
        if (!props.isRequest && currencies.length > 1) {
            toggleDisplayedContent();
            setSelectedCountry(country);
            setCurrencies(currencies);
            return;
        }
        props.onDropdownChange(props.role, country.code, currencies[0].code);
        props.toggleCountryList();
    };

    if (displayedContent === COUNTRIES) {
        return (
            <CountryListDropdown ref={dropdownListRef}>
                <SearchInputWrapper>
                    <SearchInput
                        placeholder={props.calculatorData.messages?.countryListSearchPlaceholder}
                        autoComplete="off"
                        tabIndex={'0'}
                        type="search"
                        onChange={filterCountries}
                        ref={searchInputRef}
                    />
                </SearchInputWrapper>

                <DropdownList tabIndex="0" $scrollHeight={scrollHeight}>
                    <DropdownListInner>
                        {countries.map((country, index) => (
                            <PromoCountryItem
                                key={country.id}
                                ref={(el) => (itemRefs.current[index] = el)}
                                direction={direction}
                                country={country}
                                handleCountrySelection={handleCountrySelection}
                                isSelected={currentCountryIndex === index}
                                showCurrencies={props.showCurrencies}
                                query={searchInputRef.current?.value}
                                isLongPanel
                            />
                        ))}
                        {isEmptySearchResults && (
                            <DropdownNotFound>
                                <PromoText tag="div" variant="subheader" color="gray40" ellipsis>
                                    {props.calculatorData.messages?.countryListNotFound}
                                </PromoText>
                            </DropdownNotFound>
                        )}
                    </DropdownListInner>
                </DropdownList>
            </CountryListDropdown>
        );
    }

    if (displayedContent === CURRENCIES) {
        return (
            <CountryListDropdown ref={dropdownListRef}>
                <SearchInputWrapper>
                    <SearchInput />
                    <LightDiskButton onClick={props.toggleCountryList} iconName="cross" type="button" raised={false} marginRight="8px" tabIndex={'0'} />
                </SearchInputWrapper>

                <PromoCurrencyList
                    header={props.calculatorData.messages?.countryListCurrencyHeader}
                    currencies={currencies}
                    handleCurrencySelection={handleCurrencySelection}
                />
            </CountryListDropdown>
        );
    }
};

const mapStateToProps = ({ app, extra }, ownProps) => {
    const { calculator: calculatorData, ...landingPageData } = extra.strapiApi;

    return {
        calculatorData,
        landingPageData,
        countryCode: app.country,
        ...ownProps,
    };
};

export default connect(mapStateToProps)(PromoCountryList);
